import type {CustomerProfileLifecycleBase} from "~/components/entityProfile/customerProfileDataSource/types";
import type {JourneyProgressStatus} from "~/components/journeyProgressCard/types";
import {resolveActivityLogCardIsInactiveFromLifecycleStatus} from "~/modules/activityTimeline/utils/resolveActivityLogCardIsInactive";
import type {TimelineDayGroup} from "~/modules/activityTimeline/types/activityTimeline.types";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

function buildLifecycleStatusById(
    lifecycles: Array<Pick<CustomerProfileLifecycleBase, "id" | "status">>,
): ReadonlyMap<string, JourneyProgressStatus> {
    return new Map(lifecycles.map((lifecycle) => [lifecycle.id, lifecycle.status]));
}

/** Derives `isInactive` on customer activity cards from lifecycle active/inactive status. */
export function enrichCustomerProfileActivityGroupsWithLifecycleStatus(
    groups: Array<TimelineDayGroup>,
    lifecycles: Array<Pick<CustomerProfileLifecycleBase, "id" | "status">>,
): Array<TimelineDayGroup> {
    const statusByLifecycleId = buildLifecycleStatusById(lifecycles);

    return groups.map((group) => ({
        ...group,
        items: group.items.map((item) => {
            if (item.lifecycleId === undefined) {
                return item;
            }

            const lifecycleStatus = statusByLifecycleId.get(item.lifecycleId);
            if (lifecycleStatus === undefined) {
                return item;
            }

            return {
                ...item,
                isInactive: resolveActivityLogCardIsInactiveFromLifecycleStatus(lifecycleStatus),
            };
        }),
    }));
}

/** Maps event-container id → lifecycle status when building customer activity from live data. */
export function resolveCustomerActivityIsInactive(args: {
    eventContainerId: Uuid;
    lifecycleStatusByEventContainerId: ReadonlyMap<Uuid, JourneyProgressStatus>;
}): boolean {
    const lifecycleStatus = args.lifecycleStatusByEventContainerId.get(args.eventContainerId);
    if (lifecycleStatus === undefined) {
        return false;
    }
    return resolveActivityLogCardIsInactiveFromLifecycleStatus(lifecycleStatus);
}
